import { type FC, useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';

import { useUpdateMerchant } from '@entities/merchant';
import {
  getUserAvatarInitials,
  getUserAvatarName,
  useUser,
} from '@entities/user';

import {
  Avatar,
  Box,
  Flex,
  Spinner,
  Text,
  Tooltip,
  type TypographyProps,
} from '@shared/ui-kit';

import { type Merchant } from '@api/services/merchants';

import { AssignToMeButton } from './_components';

interface Props {
  merchantId: string;
  assignedTo: Merchant['assigned_to'];
  assignedFullName: Merchant['assigned_full_name'];

  variant?: 'left' | 'right';
  fontSize?: TypographyProps['fontSize'];
}

const AssignedTo: FC<Props> = (props) => {
  const {
    merchantId,
    assignedTo,
    assignedFullName,
    variant = 'right',
    fontSize = 'sm',
  } = props;

  const t = useTranslations();
  const user = useUser();
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  const { isLoading, trigger } = useUpdateMerchant(merchantId);

  const maxNameContainerWidth = 300;
  const isAssignedToMe = assignedTo === user?.id;

  const assignedNameRef = useCallback(
    (node: HTMLParagraphElement) => {
      if (node && node?.clientWidth === maxNameContainerWidth) {
        setIsOverflowing(true);
      }
    },
    [props],
  );

  if (isLoading) {
    return (
      <Box>
        <Spinner size="md" />
      </Box>
    );
  }

  return (
    <Flex
      columnGap="3"
      alignItems="center"
      direction={variant === 'left' ? 'row-reverse' : 'row'}
    >
      <Flex
        direction="column"
        alignItems={variant === 'left' ? 'flex-start' : 'flex-end'}
      >
        {isAssignedToMe ? (
          <>
            <Text fontSize={fontSize}>
              {`${user?.last_name} ${user?.first_name}`}
            </Text>
            <Text fontSize={fontSize}>{user?.patronymic}</Text>
          </>
        ) : (
          <>
            <Tooltip
              fontSize={fontSize}
              label={
                typeof assignedFullName === 'string' && isOverflowing
                  ? assignedFullName
                  : undefined
              }
              offset={[0, -20]}
              placement="bottom-start"
              maxWidth={maxNameContainerWidth}
            >
              <Box>
                <Text
                  ref={assignedNameRef}
                  fontSize={fontSize}
                  lineHeight="short"
                  textAlign={variant === 'left' ? 'start' : 'end'}
                  maxW={maxNameContainerWidth}
                  textOverflow="ellipsis"
                  overflow="hidden"
                  whiteSpace="nowrap"
                >
                  {assignedFullName || t('General.unassignedUser')}
                </Text>
              </Box>
            </Tooltip>
            <AssignToMeButton
              merchantId={merchantId}
              triggerUpdate={trigger}
            />
          </>
        )}
      </Flex>
      <Avatar
        size="sm"
        name={
          assignedTo && assignedFullName
            ? getUserAvatarName(assignedFullName, assignedTo)
            : undefined
        }
        getInitials={getUserAvatarInitials}
      />
    </Flex>
  );
};

export default AssignedTo;
